import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';

interface Props {
  onBack: () => void;
}

export default function PrivacyPage({ onBack }: Props) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)', fontFamily: 'Inter, sans-serif' }}>
      {/* Header bar */}
      <div className="sticky top-0 z-50 px-4 py-3 border-b flex items-center gap-3"
        style={{ backgroundColor: 'var(--header-bg)', backdropFilter: 'blur(20px)', borderColor: 'var(--border-color)' }}>
        <button onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium text-[#2563EB] hover:underline transition-colors">
          <ArrowLeft size={16} />
          Volver
        </button>
        <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Política de Privacidad</span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto px-4 py-10"
      >
        <div className="rounded-2xl p-8 border" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-md)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Shield size={20} className="text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
                Política de Privacidad
              </h1>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Conforme a la Ley 1581 de 2012 y el Decreto 1074 de 2015 · Última actualización: {new Date().toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>

          <div className="prose max-w-none" style={{ color: 'var(--text-primary)' }}>

            <Section title="1. Responsable del Tratamiento">
              <p>
                <strong>SYSTELEC ELECTRONICA E INFORMATICA</strong><br />
                Carrera 8 #18-24 Of. 611, Ciudad de Pereira, Risaralda, Colombia.<br />
                Plataforma: <strong>rastreadordebienes.co</strong>
              </p>
              <p>
                De conformidad con la Ley Estatutaria 1581 de 2012 y su Decreto Reglamentario 1074 de 2015, en cumplimiento
                del derecho constitucional que tienen todas las personas a conocer, actualizar y rectificar las informaciones
                que se hayan recogido sobre ellas en bases de datos o archivos, SYSTELEC ELECTRONICA E INFORMATICA adopta
                la presente Política de Tratamiento de Datos Personales.
              </p>
            </Section>

            <Section title="2. Datos Personales Recopilados">
              <p>A través de nuestra plataforma, recopilamos los siguientes datos personales:</p>
              <ul>
                <li><strong>Del solicitante:</strong> Nombres, apellidos, tipo y número de documento de identidad, correo electrónico y número de celular.</li>
                <li><strong>De la persona a consultar:</strong> Tipo y número de documento de identidad, motivo de consulta.</li>
                <li><strong>De pago:</strong> Los datos de pago son procesados directamente por Wompi (Bancolombia S.A.) y no son almacenados en nuestros servidores.</li>
                <li><strong>Técnicos:</strong> Dirección IP, agente de usuario (User-Agent) del navegador, para efectos de seguridad y trazabilidad.</li>
              </ul>
            </Section>

            <Section title="3. Finalidad del Tratamiento">
              <p>Los datos personales recopilados serán tratados para las siguientes finalidades:</p>
              <ul>
                <li>Procesar y ejecutar la consulta de bienes y vehículos solicitada por el usuario.</li>
                <li>Enviar el informe resultante al correo electrónico registrado.</li>
                <li>Gestionar el proceso de pago a través de la pasarela Wompi.</li>
                <li>Atender solicitudes, quejas, reclamaciones y peticiones (PQRS).</li>
                <li>Cumplir obligaciones legales y regulatorias.</li>
                <li>Prevenir el fraude y garantizar la seguridad de la plataforma.</li>
              </ul>
            </Section>

            <Section title="4. Base Jurídica del Tratamiento">
              <p>
                El tratamiento de datos se realiza con base en el <strong>consentimiento libre, previo, expreso e informado</strong> del
                titular, manifestado al momento de aceptar los presentes términos y proceder con la consulta. Para los datos
                del consultado, el tratamiento se fundamenta en el interés legítimo del solicitante y en la disponibilidad
                de dichos datos en registros públicos de acceso legal.
              </p>
            </Section>

            <Section title="5. Derechos del Titular">
              <p>
                De conformidad con el artículo 8 de la Ley 1581 de 2012, usted como titular de datos personales tiene
                los siguientes derechos:
              </p>
              <ul>
                <li><strong>Conocer</strong> los datos personales que tenemos sobre usted.</li>
                <li><strong>Actualizar y rectificar</strong> sus datos cuando sean inexactos, incompletos o fragmentados.</li>
                <li><strong>Solicitar la supresión</strong> de sus datos cuando no exista obligación legal de conservarlos.</li>
                <li><strong>Revocar la autorización</strong> para el tratamiento de sus datos.</li>
                <li><strong>Acceder gratuitamente</strong> a sus datos personales que hayan sido objeto de tratamiento.</li>
                <li>Presentar quejas ante la <strong>Superintendencia de Industria y Comercio (SIC)</strong> por infracciones a la Ley 1581 de 2012.</li>
              </ul>
            </Section>

            <Section title="6. Transferencia y Transmisión de Datos">
              <p>
                Los datos personales del solicitante son compartidos con los siguientes terceros, estrictamente para
                las finalidades descritas:
              </p>
              <ul>
                <li><strong>Wompi (Bancolombia S.A.):</strong> Para el procesamiento seguro del pago.</li>
                <li><strong>n8n / Plataformas Digitales:</strong> Plataforma de automatización que procesa y ejecuta la consulta.</li>
                <li><strong>Superintendencia de Notariado y Registro:</strong> Fuente oficial de información de propiedades.</li>
                <li><strong>RUNT:</strong> Fuente oficial de información de vehículos.</li>
              </ul>
              <p>
                No realizamos transferencias internacionales de datos sin el consentimiento del titular o sin las
                garantías exigidas por la Ley 1581 de 2012.
              </p>
            </Section>

            <Section title="7. Seguridad de la Información">
              <p>
                Implementamos medidas técnicas, administrativas y organizacionales adecuadas para proteger los datos
                personales contra acceso no autorizado, pérdida, alteración o destrucción. Las transmisiones se
                realizan mediante protocolos seguros (HTTPS/TLS). Los datos de pago son procesados directamente por
                Wompi bajo estándares PCI-DSS.
              </p>
            </Section>

            <Section title="8. Tiempo de Conservación">
              <p>
                Los datos personales serán conservados durante el tiempo necesario para cumplir con las finalidades
                del tratamiento y las obligaciones legales aplicables. Una vez cumplida la finalidad, procederemos a
                la supresión segura de los datos, salvo obligación legal de conservarlos.
              </p>
            </Section>

            <Section title="9. Cookies y Tecnologías de Seguimiento">
              <p>
                Esta plataforma puede utilizar cookies técnicas necesarias para el funcionamiento del servicio.
                No utilizamos cookies de seguimiento ni publicidad comportamental de terceros sin el consentimiento
                explícito del usuario.
              </p>
            </Section>

            <Section title="10. Modificaciones a la Política">
              <p>
                Nos reservamos el derecho de modificar esta política en cualquier momento. Cualquier cambio sustancial
                será notificado a los usuarios registrados mediante correo electrónico. La versión vigente siempre
                estará disponible en esta página.
              </p>
            </Section>

            <Section title="11. Contacto y Ejercicio de Derechos">
              <p>
                Para ejercer sus derechos como titular o para cualquier consulta relacionada con el tratamiento
                de sus datos personales, puede contactarse con nosotros a través de los canales disponibles en
                <strong> rastreadordebienes.co</strong>.
              </p>
              <p>
                <strong>SYSTELEC ELECTRONICA E INFORMATICA</strong><br />
                Carrera 8 #18-24 Of. 611, Pereira, Risaralda, Colombia.
              </p>
            </Section>

          </div>
        </div>

        <div className="mt-6 text-center">
          <button onClick={onBack}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-white text-sm"
            style={{ backgroundColor: '#2563EB' }}>
            <ArrowLeft size={16} />
            Volver a la consulta
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-bold mb-3 pb-2 border-b"
        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {children}
      </div>
    </div>
  );
}
